/**
 * Phase 41 Plan 41-11 Task 2 — Surface 5a: Admin SLA Tiers (per-project CRUD).
 *
 * Per-project priority-tier configuration for SLA Support projects. Project
 * picker filters to projects whose `projectTypeId === SLA_SUPPORT_PROJECT_TYPE_ID`
 * (imported from constants/typeIds.ts per checker I-13 — no hardcoded literal).
 *
 * Threat-register notes:
 *   - T-41-33: route gated by AdminRoute (page-level) AND backend
 *     `requireAdminOrProjectPM` on POST/PATCH/DELETE (defense-in-depth).
 *   - T-41-35: tier names are plain JSX text — React escaping prevents XSS.
 *   - T-41-36: native confirm() is BANNED in v7 surfaces (checker I-19);
 *     delete uses the v7 Modal primitive + Btn variant=danger. The
 *     `tierToDelete` state holds the candidate; the Modal opens when it's
 *     non-null; confirm fires the mutation and clears state.
 */
import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TopBar } from '../../components/ui/TopBar';
import { Card } from '../../components/ui/Card';
import { Btn } from '../../components/ui/Btn';
import { Pill } from '../../components/ui/Pill';
import { Modal } from '../../components/ui/Modal';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { DropdownSelect } from '../../components/ui/DropdownSelect';
import { useToast } from '../../components/ui/Toast';
import { useProjects } from '../../hooks/useProjects';
import {
  useProjectSlaTiers,
  useCreateProjectSlaTier,
  useUpdateProjectSlaTier,
  useDeleteProjectSlaTier,
} from '../../hooks/useProjectSlaTiers';
import type { ProjectSlaTier } from '../../services/api/projectSlaTier';
import { SLA_SUPPORT_PROJECT_TYPE_ID } from '../../constants/typeIds';

// Petra UAT 2026-05-20 (test D) — converters use minutes (backend contract);
// previous version used seconds and silently produced 60× wrong values.
const hoursToMinutes = (h: number) => Math.max(0, Math.round(h * 60));
const minutesToHours = (m: number) => Math.round((m / 60) * 10) / 10;

interface TierFormState {
  name: string;
  orderIndex: number;
  responseHours: number;
  resolutionHours: number;
  responseWarningCsv: string;
  resolutionWarningCsv: string;
}

function emptyForm(): TierFormState {
  return {
    name: '',
    orderIndex: 1,
    responseHours: 4,
    resolutionHours: 24,
    responseWarningCsv: '50, 80',
    resolutionWarningCsv: '50, 80',
  };
}

function parseThresholdCsv(csv: string): number[] {
  return csv
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0 && n < 200);
}

export function SlaTiersAdminPage() {
  const projects = useProjects();
  const [searchParams] = useSearchParams();
  // Petra UAT 2026-05-20 — honor ?projectId= deep links from ProjectDetailPage's
  // "Manage SLA priority tiers" surface so the project is pre-selected. The
  // deep-linked project is always included in the picker even when the SLA
  // Support filter would have hidden it (e.g. partial-template migrations that
  // pick SLA Task without SLA Support).
  const deepLinkedId = useMemo(() => {
    const raw = searchParams.get('projectId');
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [searchParams]);
  const slaProjects = useMemo(() => {
    const all = projects.data ?? [];
    return all.filter(
      (p) =>
        p.projectTypeId === SLA_SUPPORT_PROJECT_TYPE_ID ||
        p.id === deepLinkedId,
    );
  }, [projects.data, deepLinkedId]);
  const [projectId, setProjectId] = useState<number | undefined>(
    deepLinkedId ?? slaProjects[0]?.id,
  );
  const tiers = useProjectSlaTiers(projectId);
  const createTier = useCreateProjectSlaTier();
  const updateTier = useUpdateProjectSlaTier();
  const deleteTier = useDeleteProjectSlaTier();
  const toast = useToast();

  const [editing, setEditing] = useState<ProjectSlaTier | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<TierFormState>(emptyForm());
  // Per checker I-19 — Modal-based delete confirm replaces native confirm()
  const [tierToDelete, setTierToDelete] = useState<ProjectSlaTier | null>(null);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (t: ProjectSlaTier) => {
    setEditing(t);
    setForm({
      name: t.name,
      orderIndex: t.orderIndex,
      responseHours: minutesToHours(t.responseTargetMinutes),
      resolutionHours: minutesToHours(t.resolutionTargetMinutes),
      responseWarningCsv: (t.responseWarningThresholds ?? []).join(', '),
      resolutionWarningCsv: (t.resolutionWarningThresholds ?? []).join(', '),
    });
    setFormOpen(true);
  };

  const submit = () => {
    if (!projectId) return;
    const input = {
      name: form.name.trim(),
      orderIndex: Number(form.orderIndex),
      responseTargetMinutes: hoursToMinutes(Number(form.responseHours)),
      resolutionTargetMinutes: hoursToMinutes(Number(form.resolutionHours)),
      responseWarningThresholds: parseThresholdCsv(form.responseWarningCsv),
      resolutionWarningThresholds: parseThresholdCsv(form.resolutionWarningCsv),
    };
    const onDone = () => {
      setFormOpen(false);
      toast('Tier saved', 'success');
    };
    if (editing) {
      updateTier.mutate({ projectId, id: editing.id, input }, { onSuccess: onDone });
    } else {
      createTier.mutate({ projectId, input }, { onSuccess: onDone });
    }
  };

  const remove = (t: ProjectSlaTier) => setTierToDelete(t);
  const confirmDelete = () => {
    if (!projectId || !tierToDelete) return;
    deleteTier.mutate(
      { projectId, id: tierToDelete.id },
      {
        onSuccess: () => {
          toast('Tier deleted', 'success');
          setTierToDelete(null);
        },
        onError: () => {
          toast('Failed to delete tier', 'error');
          setTierToDelete(null);
        },
      },
    );
  };

  return (
    <>
      <TopBar
        title="SLA Tiers"
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'SLA tiers' }]}
      />
      <div className="p-6 max-w-5xl mx-auto">
        <Card className="mb-4">
          <Field label="Project">
            <DropdownSelect
              value={projectId != null ? String(projectId) : ''}
              onChange={(e) => setProjectId(Number(e.target.value))}
            >
              <option value="">— Pick an SLA project —</option>
              {slaProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </DropdownSelect>
          </Field>
        </Card>

        {projectId && (
          <Card
            title="Priority tiers"
            action={
              <Btn size="sm" variant="accent" onClick={openCreate}>
                + Add tier
              </Btn>
            }
          >
            {tiers.isLoading ? (
              <div className="py-6 text-center text-ms-text-muted">Loading…</div>
            ) : (tiers.data?.data ?? []).length === 0 ? (
              <div className="py-6 text-center text-ms-text-muted">
                No tiers — add the first one.
              </div>
            ) : (
              <table className="min-w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-ms-text-muted">
                    <th className="py-2 pr-2 font-semibold">Order</th>
                    <th className="py-2 pr-2 font-semibold">Name</th>
                    <th className="py-2 pr-2 font-semibold">Response</th>
                    <th className="py-2 pr-2 font-semibold">Resolution</th>
                    <th className="py-2 pr-2 font-semibold">Response warnings</th>
                    <th className="py-2 pr-2 font-semibold">Resolution warnings</th>
                    <th className="py-2 pr-2 font-semibold"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ms-border-subtle">
                  {(tiers.data?.data ?? [])
                    .slice()
                    .sort((a, b) => a.orderIndex - b.orderIndex)
                    .map((t) => (
                      <tr key={t.id}>
                        <td className="py-2 pr-2 font-mono tabular-nums">{t.orderIndex}</td>
                        <td className="py-2 pr-2 font-medium text-ms-text-strong">{t.name}</td>
                        <td className="py-2 pr-2 tabular-nums">
                          {minutesToHours(t.responseTargetMinutes)} h
                        </td>
                        <td className="py-2 pr-2 tabular-nums">
                          {minutesToHours(t.resolutionTargetMinutes)} h
                        </td>
                        <td className="py-2 pr-2">
                          <div className="flex flex-wrap gap-1">
                            {(t.responseWarningThresholds ?? []).map((p) => (
                              <Pill key={`r-${p}`} bg="#FEF3C7" fg="#B45309">
                                {p}%
                              </Pill>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 pr-2">
                          <div className="flex flex-wrap gap-1">
                            {(t.resolutionWarningThresholds ?? []).map((p) => (
                              <Pill key={`x-${p}`} bg="#FEF3C7" fg="#B45309">
                                {p}%
                              </Pill>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 pr-2">
                          <div className="flex gap-1">
                            <Btn size="sm" variant="ghost" onClick={() => openEdit(t)}>
                              Edit
                            </Btn>
                            <Btn size="sm" variant="ghost" onClick={() => remove(t)}>
                              Delete
                            </Btn>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </Card>
        )}

        {/* Per checker I-19 — Modal-based delete confirmation (T-41-36 mitigation) */}
        <Modal
          open={tierToDelete !== null}
          onClose={() => setTierToDelete(null)}
          title="Delete SLA tier"
          footer={
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setTierToDelete(null)}>
                Cancel
              </Btn>
              <Btn variant="danger" onClick={confirmDelete}>
                Delete
              </Btn>
            </div>
          }
        >
          <p className="text-[12.5px] text-ms-text-secondary">
            Delete tier{' '}
            <span className="font-semibold text-ms-text-strong">{tierToDelete?.name}</span>? This
            cannot be undone. Existing tasks on this tier remain but lose their tier assignment.
          </p>
        </Modal>

        <Modal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          title={editing ? 'Edit tier' : 'Add tier'}
          footer={
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setFormOpen(false)}>
                Cancel
              </Btn>
              <Btn variant="accent" onClick={submit}>
                Save
              </Btn>
            </div>
          }
        >
          <div className="space-y-3">
            <Field label="Name">
              <TextInput
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Order index (1 = highest)">
              <TextInput
                type="number"
                min={1}
                value={form.orderIndex}
                onChange={(e) => setForm({ ...form, orderIndex: Number(e.target.value) })}
              />
            </Field>
            <Field label="Response target (hours)">
              <TextInput
                type="number"
                min={0}
                step={0.5}
                value={form.responseHours}
                onChange={(e) => setForm({ ...form, responseHours: Number(e.target.value) })}
              />
            </Field>
            <Field label="Resolution target (hours)">
              <TextInput
                type="number"
                min={0}
                step={0.5}
                value={form.resolutionHours}
                onChange={(e) => setForm({ ...form, resolutionHours: Number(e.target.value) })}
              />
            </Field>
            <Field label="Response warning thresholds (comma-separated %)" hint="e.g. 50, 80">
              <TextInput
                value={form.responseWarningCsv}
                onChange={(e) => setForm({ ...form, responseWarningCsv: e.target.value })}
              />
            </Field>
            <Field label="Resolution warning thresholds (comma-separated %)" hint="e.g. 50, 80">
              <TextInput
                value={form.resolutionWarningCsv}
                onChange={(e) => setForm({ ...form, resolutionWarningCsv: e.target.value })}
              />
            </Field>
          </div>
        </Modal>
      </div>
    </>
  );
}
